import { i18n } from '@lingui/core';
import { Loader, Text, List, Button, Select, Space, Center, Divider } from '@mantine/core';
import { closeAllModals } from '@mantine/modals';
import { IconChevronDown, IconSend, IconTemplate } from '@tabler/icons';
import { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { Contact } from '../../../../Service/ContactS';
import { EventBaseInput, EventRichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { DataS } from '../../../../Service/DataS';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { useRecommendationMail } from '../../../../Service/hooks/useRecommendationMail';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { RecommendationDto } from '../../../../Types/RecommendationT';
import { StringUtils } from '../../../../Utils/utils';
import { EventContext } from '../../../../Service/Context/EventContext';
import { EventS } from '../../../../Service/EventS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import AttachmentsAlert from './AttachmentsAlert';
import { TiptapS } from '../../../../Service/TiptapS';

interface Props {
  recommendation: RecommendationDto;
  reload: () => void;
  provider: Contact;
}

const SendAvailabilityMailModal: FC<Props> = ({ recommendation, reload, provider }) => {
  const [loading, setLoading] = useState(false);
  const { dates } = useContext<any>(EventContext);
  const { id: eventId } = useParams();
  const { profileId } = useJwtClaims();
  const { templates } = useAgencyTemplates();
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const recommendationDates = RecommendationS.extractRecommendationDates(dates, recommendation);
  const [showAttachmentsAlert, setShowAttachmentsAlert] = useState(false);

  const { mail, attachments, subject, message, updateProp, lastUpdate } = useRecommendationMail(
    'CHECK_AVAILABILITY',
    recommendation,
    selectedTemplate,
  );

  useEffect(() => {
    if (selectedTemplate) {
      if (selectedTemplate.attachments.length > 0) {
        setShowAttachmentsAlert(true);
      } else {
        setShowAttachmentsAlert(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const checkAvailability = async () => {
    setLoading(true);
    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId: Number(eventId),
      message,
      subject,
    });
    if (placeholdersResolved) {
      await RecommendationS.checkAvailabilities({
        recommendationsIds: [recommendation.id],
        ...mail,
        attachments,
        newVersion: true,
      });
      closeAllModals();
      reload();
    }
    setLoading(false);
  };

  return (
    <>
      <Text>So funktionierts:</Text>
      <List mt="md" mb="xl" type="ordered" size="xs" spacing={5}>
        <List.Item>Es wird eine E-Mail an den Dienstleister gesendet.</List.Item>
        <List.Item>Du wirst benachrichtigt, sobald der Dienstleister geantwortet hat.</List.Item>
        <List.Item>Ist er an dem Tag verfügbar, kannst Du ihm die Event-Infos übermitteln.</List.Item>
      </List>
      <Divider my="sm" />
      <Select
        mb="xs"
        label={
          <Text size="xs" color="dimmed">
            Vorlage
          </Text>
        }
        nothingFound="Keine Vorlagen"
        icon={<IconTemplate size={20} />}
        data={DataS.getAgencyTemplateOptions(templates)}
        value={selectedTemplate ? selectedTemplate.id : undefined}
        onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
        styles={{ rightSection: { pointerEvents: 'none' } }}
        rightSection={<IconChevronDown size={14} />}
        rightSectionWidth={25}
      />
      {showAttachmentsAlert && <AttachmentsAlert />}
      <EventBaseInput
        updateTrigger={lastUpdate}
        toolbar={<BaseToolbar />}
        label="Betreff"
        value={subject}
        onUpdate={(html) => updateProp({ subject: html })}
      />
      <Space h="sm" />
      <Text mb="sm">{`Hey ${RecommendationS.getGreeting(provider)},`} </Text>
      <Text mb="sm">
        {`ich möchte Dich für ein Event am ${StringUtils.concatStrings(
          recommendationDates.map(EventS.formatEventDate),
        )} vermitteln - bist Du noch verfügbar?`}{' '}
      </Text>
      <EventRichInput
        updateTrigger={lastUpdate}
        isEmailInput
        styles={TiptapS.TextAreaStyles}
        label="Nachricht an den Dienstleister"
        value={message}
        onUpdate={(html) => updateProp({ message: html })}
        toolbar={
          <TemplateMessageToolbar
            saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments } })}
          />
        }
      />
      <Center mt="xl">
        <Button
          mb="xs"
          onClick={checkAvailability}
          disabled={loading}
          leftIcon={!loading ? <IconSend size={16} /> : <Loader size="xs" />}
          sx={{ width: 250 }}
        >
          {i18n._('recommendation.check.availability')}
        </Button>
      </Center>
    </>
  );
};

export default SendAvailabilityMailModal;
